/**
 * TakeoffLinkModal — link a PDF measurement to a timber item.
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Plus } from 'lucide-react';
import { db } from '../../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  subscribeToGlulamItems,
  subscribeToTimberItems,
  type GlulamItem,
  type TimberItem,
} from '../../utils/materialsDatabaseService';
import { buildProposalTimberSpeciesOptions } from '../../utils/proposalTimberCatalog';
import { createTakeoffStructure } from '../../utils/pdfTakeoffStructureService';
import type { PdfMeasurement, PdfTakeoffLink, PdfTakeoffStructure, ProposalBuilderData, ProposalNodeSet } from '../../types';
import {
  composeDimensions,
  formatTimberCurrency,
  mergeTakeoffStructureOptions,
  parseDimensions,
  parseDimensionsParts,
  roundTimberLengthFt,
  defaultTimberOrderLengthFt,
  timberDefaultsFromTakeoffLink,
} from '../../utils/timberTakeoffHelpers';
import {
  inferTimberDefaultsForTakeoffStructure,
  timberDefaultsFromFields,
  type ProposalTimberDefaults,
} from '../../utils/proposalTimberDefaults';
import './TakeoffLinkModal.css';

const MEMBER_PRESETS = ['Beam', 'Post', 'Rafter', 'Brace', 'Purlin', 'Plate', 'Girt'];

export interface TakeoffLinkFormData {
  structureId: string;
  structureName: string;
  name: string;
  species: string;
  grade: string;
  dimensions: string;
  crossHeight: string;
  crossWidth: string;
  lengthFt: string;
  quantity: number;
  moistureContent: '' | 'green' | 'kiln_dried';
  unitCost: number;
  notes: string;
}

export interface TakeoffLinkModalProps {
  measurement: PdfMeasurement;
  measuredLengthFt: number | null;
  dealId: string;
  leadId: string;
  draftStructures: PdfTakeoffStructure[];
  userId: string;
  userName: string;
  existingLink?: PdfTakeoffLink | null;
  initialStructureId?: string | null;
  /** Closest existing take-off on this page — used to prefill cross-section defaults. */
  nearestTakeoffLink?: PdfTakeoffLink | null;
  /** Items already linked in this take-off session (for per-structure defaults). */
  sessionLinks?: PdfTakeoffLink[];
  recommendedTimberDefaults?: ProposalTimberDefaults | null;
  onRememberTimberDefaults?: (defaults: ProposalTimberDefaults) => void;
  onSave: (data: TakeoffLinkFormData, action: 'add' | 'add_next') => void | Promise<void>;
  onSkip: () => void;
  onCancel: (deleteMeasurement: boolean) => void;
  onClose: () => void;
}

export function TakeoffLinkModal({
  measurement,
  measuredLengthFt,
  dealId,
  leadId,
  draftStructures,
  userId,
  userName,
  existingLink,
  initialStructureId,
  nearestTakeoffLink = null,
  sessionLinks = [],
  recommendedTimberDefaults = null,
  onRememberTimberDefaults,
  onSave,
  onSkip,
  onCancel,
  onClose,
}: TakeoffLinkModalProps) {
  const [proposalNodeSets, setProposalNodeSets] = useState<ProposalNodeSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [timberItems, setTimberItems] = useState<TimberItem[]>([]);
  const [glulamItems, setGlulamItems] = useState<GlulamItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [creatingStructure, setCreatingStructure] = useState(false);
  const [deleteOnCancel, setDeleteOnCancel] = useState(false);
  const [newStructureName, setNewStructureName] = useState('');

  const existingParts = parseDimensionsParts(existingLink?.dimensions || '');

  const initialLength =
    existingParts.length ||
    (measuredLengthFt != null ? String(defaultTimberOrderLengthFt(measuredLengthFt)) : '');

  const structureOptions = useMemo(
    () => mergeTakeoffStructureOptions(draftStructures, proposalNodeSets),
    [draftStructures, proposalNodeSets],
  );

  const [structureId, setStructureId] = useState(existingLink?.structureId || '');
  const [name, setName] = useState(existingLink?.name || '');
  const [species, setSpecies] = useState(existingLink?.species || '');
  const [grade, setGrade] = useState(existingLink?.grade || '');
  const [crossHeight, setCrossHeight] = useState(existingParts.height);
  const [crossWidth, setCrossWidth] = useState(existingParts.width);
  const [lengthFt, setLengthFt] = useState(initialLength);
  const [quantity, setQuantity] = useState(existingLink?.quantity ?? 1);
  const [moistureContent, setMoistureContent] = useState<'' | 'green' | 'kiln_dried'>(existingLink?.moistureContent ?? '');
  const [unitCost, setUnitCost] = useState(existingLink?.unitCost ?? 0);
  const [notes, setNotes] = useState(existingLink?.notes || '');
  const [usingRecommendedTimber, setUsingRecommendedTimber] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const appliedDefaultsForStructureRef = useRef<string | null>(null);

  const resolveDefaultsForStructure = useCallback(
    (sid: string): ProposalTimberDefaults | null =>
      inferTimberDefaultsForTakeoffStructure(sid, sessionLinks, recommendedTimberDefaults),
    [sessionLinks, recommendedTimberDefaults],
  );

  const applyTimberDefaults = useCallback(
    (defaults: ProposalTimberDefaults, fromRecommendation: boolean) => {
      setSpecies(defaults.species);
      const grades = timberItems
        .filter(i => i.species === defaults.species)
        .map(i => i.grade)
        .filter(Boolean);
      const uniqueGrades = Array.from(new Set(grades));
      setGrade(defaults.grade && uniqueGrades.includes(defaults.grade) ? defaults.grade : uniqueGrades[0] || '');
      setMoistureContent(defaults.moistureContent);
      setUsingRecommendedTimber(fromRecommendation);
    },
    [timberItems],
  );

  useEffect(() => {
    if (existingLink || !structureId || timberItems.length === 0) return;
    if (appliedDefaultsForStructureRef.current === structureId && species) return;
    const defaults = resolveDefaultsForStructure(structureId);
    if (!defaults) return;
    appliedDefaultsForStructureRef.current = structureId;
    const fromStructure = sessionLinks.some(l => l.structureId === structureId && l.species?.trim());
    applyTimberDefaults(defaults, !fromStructure && !!recommendedTimberDefaults);
  }, [
    existingLink,
    structureId,
    species,
    timberItems.length,
    sessionLinks,
    resolveDefaultsForStructure,
    applyTimberDefaults,
    recommendedTimberDefaults,
  ]);

  useEffect(() => {
    if (existingLink) return;
    appliedDefaultsForStructureRef.current = null;
    setUsingRecommendedTimber(false);
  }, [existingLink, measurement.id]);

  useEffect(() => {
    const unsubTimber = subscribeToTimberItems(
      list => setTimberItems(list),
      err => console.error('Timber subscription error:', err),
    );
    const unsubGlulam = subscribeToGlulamItems(
      list => setGlulamItems(list),
      err => console.error('Glulam subscription error:', err),
    );
    return () => {
      unsubTimber();
      unsubGlulam();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const ref = doc(db, 'proposalBuilders', dealId);
        const snap = await getDoc(ref);
        if (!cancelled && snap.exists()) {
          const data = snap.data() as ProposalBuilderData;
          setProposalNodeSets(data.nodeSets || []);
        } else if (!cancelled) {
          setProposalNodeSets([]);
        }
      } catch (err) {
        console.error('Error loading proposal:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [dealId]);

  // Reset form when linking a new measurement (not when editing an existing link).
  useEffect(() => {
    if (existingLink) return;
    setStructureId('');
    setName('');
    setSpecies('');
    setGrade('');
    setCrossHeight('');
    setCrossWidth('');
    setLengthFt('');
    setQuantity(1);
    setMoistureContent('');
    setUnitCost(0);
    setNotes('');
    setSaveError(null);
    appliedDefaultsForStructureRef.current = null;
    setUsingRecommendedTimber(false);
  }, [measurement.id, existingLink?.id]);

  // Apply measured length once page scale/size is ready (may arrive after modal opens).
  useEffect(() => {
    if (existingLink || measuredLengthFt == null) return;
    setLengthFt(prev => {
      if (prev.trim()) return prev;
      return String(defaultTimberOrderLengthFt(measuredLengthFt));
    });
  }, [existingLink, measuredLengthFt, measurement.id]);

  // Auto-select structure when proposal/draft structures finish loading — only while unset.
  useEffect(() => {
    if (existingLink || loading || structureId || structureOptions.length === 0) return;

    const preferredStructureId =
      (initialStructureId && structureOptions.some(s => s.id === initialStructureId) && initialStructureId) ||
      (nearestTakeoffLink?.structureId &&
        structureOptions.some(s => s.id === nearestTakeoffLink.structureId) &&
        nearestTakeoffLink.structureId) ||
      structureOptions[0]?.id ||
      '';

    if (!preferredStructureId) return;

    setStructureId(preferredStructureId);

    const template =
      nearestTakeoffLink && nearestTakeoffLink.structureId === preferredStructureId
        ? nearestTakeoffLink
        : null;
    if (template) {
      const defaults = timberDefaultsFromTakeoffLink(template);
      setSpecies(defaults.species);
      setGrade(defaults.grade);
      setCrossHeight(defaults.crossHeight);
      setCrossWidth(defaults.crossWidth);
      setMoistureContent(defaults.moistureContent);
      setUnitCost(defaults.unitCost);
      setName('');
      appliedDefaultsForStructureRef.current = preferredStructureId;
      setUsingRecommendedTimber(false);
    }
  }, [
    existingLink,
    loading,
    structureId,
    structureOptions,
    initialStructureId,
    nearestTakeoffLink,
  ]);

  const speciesOptions = useMemo(
    () => buildProposalTimberSpeciesOptions(timberItems, glulamItems, species ? [species] : []),
    [timberItems, glulamItems, species],
  );

  const gradeOptions = useMemo(() => {
    const filtered = species
      ? timberItems.filter(i => i.species === species)
      : timberItems;
    return Array.from(new Set(filtered.map(i => i.grade).filter(Boolean))).sort();
  }, [timberItems, species]);

  const crossSectionOptions = useMemo(() => {
    const filtered = timberItems.filter(i => {
      if (species && i.species !== species) return false;
      if (grade && i.grade !== grade) return false;
      return true;
    });
    const seen = new Set<string>();
    const opts: { height: string; width: string; label: string; unitCost: number }[] = [];
    for (const item of filtered) {
      const parts = parseDimensionsParts(item.dimensions);
      if (!parts.height || !parts.width) continue;
      const key = `${parts.height}x${parts.width}`;
      if (seen.has(key)) continue;
      seen.add(key);
      opts.push({
        height: parts.height,
        width: parts.width,
        label: `${parts.height}×${parts.width}`,
        unitCost: item.piecePrice,
      });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [timberItems, species, grade]);

  const dimensions = composeDimensions(crossHeight, crossWidth, lengthFt);
  const boardFeet = parseDimensions(dimensions) * quantity;
  const totalCost = unitCost * quantity;
  const structureName = structureOptions.find(s => s.id === structureId)?.name || '';

  const applyCrossSection = (h: string, w: string, cost?: number) => {
    setCrossHeight(h);
    setCrossWidth(w);
    if (cost != null && cost > 0) setUnitCost(cost);
  };

  const buildFormData = (): TakeoffLinkFormData => ({
    structureId,
    structureName,
    name,
    species,
    grade,
    dimensions,
    crossHeight,
    crossWidth,
    lengthFt,
    quantity,
    moistureContent,
    unitCost,
    notes,
  });

  const handleCreateStructure = async () => {
    const trimmed = newStructureName.trim();
    if (!trimmed || !userId) return;
    setCreatingStructure(true);
    try {
      const id = await createTakeoffStructure({
        dealId,
        leadId,
        name: trimmed,
        userId,
        userName,
      });
      setStructureId(id);
      setNewStructureName('');
      appliedDefaultsForStructureRef.current = null;
    } catch (err) {
      console.error('Error creating takeoff structure:', err);
    } finally {
      setCreatingStructure(false);
    }
  };

  const handleSave = async (action: 'add' | 'add_next') => {
    if (!structureId) {
      setSaveError('Select or create a structure before saving.');
      return;
    }
    if (!dimensions) {
      setSaveError('Enter width, height, and length for this timber piece.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const remembered = timberDefaultsFromFields(species, grade, moistureContent);
      if (remembered) onRememberTimberDefaults?.(remembered);
      await onSave(buildFormData(), action);
    } catch (err) {
      console.error('Error saving take-off link:', err);
      setSaveError('Failed to save take-off item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const measurementLabel = measurement.kind === 'linear' ? 'Linear' : measurement.kind;
  const hasStructures = structureOptions.length > 0;

  const formBody = (
    <>
      <div className="takeoff-link-body">
        <div className="takeoff-link-row">
          <label className="takeoff-link-label">Structure</label>
          {hasStructures ? (
            <select
              className="takeoff-link-input"
              value={structureId}
              onChange={e => {
                const nextId = e.target.value;
                setStructureId(nextId);
                if (!existingLink) {
                  appliedDefaultsForStructureRef.current = null;
                }
              }}
            >
              {structureOptions.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.source === 'draft' ? ' (take-off)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <p className="takeoff-link-hint">Add a structure below to start linking timber.</p>
          )}
        </div>

        <div className="takeoff-link-create-structure">
          <input
            className="takeoff-link-input"
            type="text"
            value={newStructureName}
            onChange={e => setNewStructureName(e.target.value)}
            placeholder={hasStructures ? 'New structure name…' : 'e.g. Main Barn'}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleCreateStructure();
              }
            }}
          />
          <button
            type="button"
            className="takeoff-link-add-structure-btn"
            disabled={creatingStructure || !newStructureName.trim()}
            onClick={() => void handleCreateStructure()}
          >
            {creatingStructure ? <Loader2 size={14} className="spin" /> : <Plus size={14} />}
            Add structure
          </button>
        </div>

        <div className="takeoff-link-row">
          <label className="takeoff-link-label">Member name</label>
          <input
            className="takeoff-link-input"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Main Beam"
            disabled={!structureId}
          />
        </div>

        <div className="takeoff-link-presets">
          {MEMBER_PRESETS.map(preset => (
            <button
              key={preset}
              type="button"
              className={`takeoff-link-preset ${name === preset ? 'active' : ''}`}
              onClick={() => setName(preset)}
              disabled={!structureId}
            >
              {preset}
            </button>
          ))}
        </div>

        <div className="takeoff-link-grid">
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Species</label>
            <select
              className="takeoff-link-input"
              value={species}
              onChange={e => {
                setSpecies(e.target.value);
                setUsingRecommendedTimber(false);
              }}
              disabled={!structureId}
            >
              <option value="">— Species —</option>
              {speciesOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Grade</label>
            <select
              className="takeoff-link-input"
              value={grade}
              onChange={e => {
                setGrade(e.target.value);
                setUsingRecommendedTimber(false);
              }}
              disabled={!structureId}
            >
              <option value="">— Grade —</option>
              {gradeOptions.map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
        </div>

        {usingRecommendedTimber && species && (
          <p className="takeoff-link-hint">Using species, grade, and dryness from your last structure.</p>
        )}

        {crossSectionOptions.length > 0 && (
          <div className="takeoff-link-presets">
            {crossSectionOptions.slice(0, 8).map(opt => (
              <button
                key={opt.label}
                type="button"
                className={`takeoff-link-preset ${crossHeight === opt.height && crossWidth === opt.width ? 'active' : ''}`}
                onClick={() => applyCrossSection(opt.height, opt.width, opt.unitCost)}
                disabled={!structureId}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        <div className="takeoff-link-grid takeoff-link-grid-3">
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Width (in)</label>
            <input
              className="takeoff-link-input"
              type="text"
              value={crossWidth}
              onChange={e => setCrossWidth(e.target.value)}
              placeholder="8"
              disabled={!structureId}
            />
          </div>
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Height (in)</label>
            <input
              className="takeoff-link-input"
              type="text"
              value={crossHeight}
              onChange={e => setCrossHeight(e.target.value)}
              placeholder="8"
              disabled={!structureId}
            />
          </div>
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Length (ft)</label>
            <input
              className="takeoff-link-input"
              type="text"
              value={lengthFt}
              onChange={e => setLengthFt(e.target.value)}
              placeholder="12"
              disabled={!structureId}
            />
          </div>
        </div>

        {dimensions && (
          <p className="takeoff-link-dim-preview">Dimensions: <strong>{dimensions}</strong></p>
        )}

        <div className="takeoff-link-grid">
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Quantity</label>
            <input
              className="takeoff-link-input"
              type="number"
              min={1}
              value={quantity}
              onChange={e => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              disabled={!structureId}
            />
          </div>
          <div className="takeoff-link-row">
            <label className="takeoff-link-label">Moisture</label>
            <select
              className="takeoff-link-input"
              value={moistureContent}
              onChange={e => {
                setMoistureContent(e.target.value as '' | 'green' | 'kiln_dried');
                setUsingRecommendedTimber(false);
              }}
              disabled={!structureId}
            >
              <option value="">—</option>
              <option value="green">Green</option>
              <option value="kiln_dried">Kiln dried</option>
            </select>
          </div>
        </div>

        <div className="takeoff-link-row">
          <label className="takeoff-link-label">Unit cost ($)</label>
          <input
            className="takeoff-link-input"
            type="number"
            min={0}
            step={0.01}
            value={unitCost || ''}
            onChange={e => setUnitCost(Math.max(0, parseFloat(e.target.value) || 0))}
            disabled={!structureId}
          />
        </div>

        <div className="takeoff-link-row">
          <label className="takeoff-link-label">Notes</label>
          <input
            className="takeoff-link-input"
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Optional"
            disabled={!structureId}
          />
        </div>

        {structureId && (
          <div className="takeoff-link-summary">
            <span>Board feet: <strong>{boardFeet.toFixed(1)}</strong></span>
            <span>Total: <strong>{formatTimberCurrency(totalCost)}</strong></span>
          </div>
        )}
        {saveError && <p className="takeoff-link-error">{saveError}</p>}
      </div>

      <footer className="takeoff-link-footer">
        {!existingLink && (
          <label className="takeoff-link-delete-check">
            <input
              type="checkbox"
              checked={deleteOnCancel}
              onChange={e => setDeleteOnCancel(e.target.checked)}
            />
            Delete measurement on cancel
          </label>
        )}
        <div className="takeoff-link-footer-actions">
          {!existingLink && (
            <button type="button" className="takeoff-link-btn takeoff-link-btn-secondary" onClick={onSkip}>
              Skip
            </button>
          )}
          <button
            type="button"
            className="takeoff-link-btn takeoff-link-btn-secondary"
            onClick={() => onCancel(deleteOnCancel)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="takeoff-link-btn takeoff-link-btn-primary"
            disabled={saving || !structureId || !dimensions}
            onClick={() => void handleSave('add')}
          >
            {saving ? <Loader2 size={16} className="spin" /> : null}
            {existingLink ? 'Update' : 'Add to take-off'}
          </button>
          {!existingLink && (
            <button
              type="button"
              className="takeoff-link-btn takeoff-link-btn-primary"
              disabled={saving || !structureId || !dimensions}
              onClick={() => void handleSave('add_next')}
            >
              Add &amp; draw next
            </button>
          )}
        </div>
      </footer>
    </>
  );

  return createPortal(
    <div className="takeoff-link-overlay" onClick={onClose}>
      <div className="takeoff-link-modal" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="takeoff-link-title">
        <header className="takeoff-link-header">
          <div>
            <h2 id="takeoff-link-title" className="takeoff-link-title">
              {existingLink ? 'Edit Take-off Item' : 'Link Measurement to Timber'}
            </h2>
            <p className="takeoff-link-subtitle">
              {measurementLabel} on page {measurement.pageNumber}
              {measuredLengthFt != null && ` · ${roundTimberLengthFt(measuredLengthFt).toFixed(2)} ft measured`}
            </p>
          </div>
          <button type="button" className="takeoff-link-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {loading ? (
          <div className="takeoff-link-loading">
            <Loader2 size={24} className="spin" />
            <span>Loading…</span>
          </div>
        ) : (
          formBody
        )}
      </div>
    </div>,
    document.body,
  );
}

export default TakeoffLinkModal;
